import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, OnDestroy } from '@angular/core';
import {
  NavigationEnd,
  Router,
  RouterModule,
  RouterOutlet,
  Event,
  ActivatedRoute
} from '@angular/router';
import { Meta } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';

import { fetchAuthSession } from 'aws-amplify/auth';
import { filter, takeUntil, switchMap, map } from 'rxjs/operators';
import { Subject, from } from 'rxjs';

import { AuthService } from './auth/auth.service';
import { AnalyticsService } from './shared/services/analytics.service';
import { ToastService } from './shared/services/toast.service';
import { ProfileService } from './shared/services/profile.service';
import { ReferralTrackingService } from './shared/services/referral-tracking.service';

import { AuthFormComponent } from './shared/components/auth-form/auth-form.component';
import { AgeVerificationComponent } from './components/age-verification/age-verification.component';
import { ToastComponent } from './shared/components/toast/toast.component';
import { NavigationBarComponent } from './components/navigation-bar/navigation-bar.component';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';

import { IToastConfig } from './shared/interfaces/toast-config.interfaces';

import { EToastPosition } from './shared/enums/toast.enums';

import { environment } from 'environments/environment';

@Component({
  selector: 'stxt-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterModule,
    HeaderComponent,
    AuthFormComponent,
    FooterComponent,
    ToastComponent,
    NavigationBarComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  title: string = 'stxt-fe-main';
  isLander: boolean = false;
  isChat: boolean = false;
  showFooter: boolean = false;
  ageVerified = localStorage.getItem('ageVerified');
  currentToast: IToastConfig | null = null;
  ngbModalOptions: NgbModalOptions = {
    backdrop: 'static',
    keyboard: false
  };
  EToastPosition = EToastPosition;

  private readonly destroy$ = new Subject<void>();

  constructor(
    readonly authService: AuthService,
    readonly modal: NgbModal,
    readonly route: ActivatedRoute,
    readonly analyticsService: AnalyticsService,
    readonly router: Router,
    readonly meta: Meta,
    readonly toastService: ToastService,
    readonly profileService: ProfileService,
    readonly referralTrackingService: ReferralTrackingService
  ) {
    this.subscribeToRouterEvents();
  }

  ngOnInit(): void {
    this.checkAgeVerification();
    this.authService.checkUserCredentials();
    this.subscribeToRouterTracking();
    this.checkMetaTags();
    this.subscribeToGlobalToasts();
    this.subscribeToQueryParams();
    this.referralTrackingService.checkAndSaveForAuthenticatedUser();
  }

  checkScreenType(url: string): boolean {
    return !url.includes('/generate') && !url.includes('/chat');
  }

  subscribeToRouterTracking(): void {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.trackPage(event.urlAfterRedirects);
      }
    });
  }

  subscribeToRouterEvents(): void {
    this.router.events
      .pipe(
        filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.showFooter = this.checkScreenType(event.url);
        this.isChat = event.url.includes('/chat');
      });
  }

  checkMetaTags(): void {
    if (
      environment.serviceEnvironment === 'staging' ||
      environment.serviceEnvironment === 'development'
    ) {
      this.meta.addTag({ name: 'robots', content: 'noindex, nofollow' });
    }
  }

  subscribeToGlobalToasts(): void {
    this.toastService.toast$.pipe(takeUntil(this.destroy$)).subscribe(toast => {
      this.currentToast = toast;
    });
  }

  subscribeToQueryParams(): void {
    this.route.queryParams
      .pipe(
        switchMap(params =>
          from(fetchAuthSession()).pipe(map(authSession => ({ params, authSession })))
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(({ params, authSession }) => {
        if (authSession.userSub) return;

        const creatorId = params['creatorId'];
        const onOpen = params['onOpen'];
        if (creatorId) {
          const modalRef = this.modal.open(AuthFormComponent);
          modalRef.componentInstance.formType = 'Lander';
          modalRef.componentInstance.isSignUp = true;
          modalRef.componentInstance.creatorId = creatorId;
        } else if (onOpen) {
          const modalRef = this.modal.open(AuthFormComponent);
          modalRef.componentInstance.isSignUp = true;
        }
      });
  }

  onCloseToast(): void {
    this.toastService.hideToast();
  }

  trackPage(url: string) {
    const urlTree = this.router.parseUrl(url);
    const queryParams = urlTree.queryParams;
    const pageName = this.getPageName(url);
    const referrer = document.referrer;
    const title = document.title;
    const path = this.router.url;
    const utmCampaign = queryParams['utm_campaign'] || '';
    const utmContent = queryParams['utm_content'] || '';
    const utmMedium = queryParams['utm_medium'] || '';
    const utmSource = queryParams['utm_source'] || '';

    this.analyticsService.trackPageLoaded(
      pageName,
      path,
      referrer,
      title,
      url,
      utmCampaign,
      utmContent,
      utmMedium,
      utmSource
    );
  }

  private getPageName(url: string): string {
    const segments = url.split('/');
    return segments[segments.length - 1] || 'Home';
  }

  checkAgeVerification(): void {
    if (this.ageVerified !== 'true') {
      this.modal.open(AgeVerificationComponent, this.ngbModalOptions);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
